import { defineHttpRpc } from '@iwentsourcing/core/rpc/server/http-rpc.js';
import { rpcApiDefinition } from './rpc-api-definition.js';

export const rpcApiHttpDefinition = defineHttpRpc(rpcApiDefinition, {
  'auth.login': {
    method: 'POST',
    path: '/api/{tenant}/{realm}/auth/{provider}/login',
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      username: { type: 'body' },
      password: { type: 'body' },
      provider: { type: 'path' },
    },
    openapi: {
      operationId: 'authLogin',
      description: 'Login to an account',
      tags: ['auth'],
    },
  },
  'auth.token': {
    method: 'POST',
    path: `/api/{tenant}/{realm}/auth/{provider}/token`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      provider: { type: 'path' },
      token: { type: 'body' },
    },
    openapi: {
      operationId: 'authToken',
      description: 'Claim a token for an account',
      tags: ['auth'],
    },
  },
  'auth.deviceCode': {
    method: 'POST',
    path: `/api/{tenant}/{realm}/auth/{provider}/device`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      provider: { type: 'path' },
    },
    openapi: {
      operationId: 'authDeviceCode',
      description: 'Claim a token for an account',
      tags: ['auth'],
    },
  },
  'auth.authorize': {
    method: 'POST',
    path: `/api/{tenant}/{realm}/auth/{provider}/authorize`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      provider: { type: 'path' },
      code: { type: 'body' },
      redirectUri: { type: 'body' },
    },
    openapi: {
      operationId: 'authAuthorize',
      description: 'Claim a token for an account',
      tags: ['auth'],
    },
  },
  'auth.register': {
    method: 'POST',
    path: `/api/{tenant}/{realm}/auth/{provider}/register`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      username: { type: 'body' },
      password: { type: 'body' },
      provider: { type: 'path' },
    },
    openapi: {
      operationId: 'authRegister',
      description: 'Register an account',
      tags: ['auth'],
    },
  },
  'auth.refresh': {
    method: 'POST',
    path: `/api/{tenant}/{realm}/auth/{provider}/refresh`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      refreshToken: { type: 'body' },
      provider: { type: 'path' },
      username: { type: 'body' },
    },
    openapi: {
      operationId: 'authRefresh',
      description: 'Refresh an access token',
      tags: ['auth'],
    },
  },
  'auth.publicProviders': {
    method: 'GET',
    path: '/api/{tenant}/{realm}/public/auth/providers',
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
    },
    openapi: {
      operationId: 'authPublicProviders',
      description: 'Public auth provider metadata (no secrets)',
      tags: ['auth'],
    },
  },
  'event.get': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/stream/{pattern}/{version}/event/{id}`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      id: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
    },
    openapi: {
      tags: ['event'],
      operationId: 'getEvent',
      description: 'Get event from stream',
    },
  },
  'event.iterate': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/stream/{pattern}/{version}/observe/event`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
      date: { type: 'query' },
    },
    openapi: {
      tags: ['event'],
      operationId: 'iterateEvents',
      description: 'Iterate event from stream',
    },
  },
  'event.range': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/stream/{pattern}/{version}/event`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
      date: { type: 'query' },
    },
    openapi: {
      tags: ['event'],
      operationId: 'rangeEvents',
      description: 'Range over events from stream',
    },
  },
  'event.push': {
    method: 'POST',
    path: `/api/{tenant}/{realm}/stream/{pattern}/{version}/event/{event}`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      event: { type: 'path' },
      token: { type: 'header' },
      await: { type: 'query' },
      args: { type: 'header' },
      id: { type: 'query' },
      data: { type: 'body' },
      annotations: { type: 'body' },
      createdAt: { type: 'body' },
    },
    openapi: {
      tags: ['event'],
      operationId: 'pushEvent',
      description: 'Push event to stream',
    },
  },
  'event.pushBatch': {
    method: 'POST',
    path: `/api/{tenant}/{realm}/stream/{pattern}/{version}/event`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      events: { type: 'body' },
      token: { type: 'header' },
      await: { type: 'query' },
      args: { type: 'header' },
    },
    openapi: {
      tags: ['event'],
      operationId: 'pushEvent',
      description: 'Push event to stream',
    },
  },
  'state.browse': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/state/{type}/{pattern}/{version}/{state}/browse`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      type: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      state: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
      take: { type: 'query' },
      open: { type: 'query' },
      direction: { type: 'query' },
      bookmark: { type: 'query' },
    },
    openapi: {
      tags: ['state'],
      operationId: 'browseStateRecords',
      description: 'Get state record',
    },
  },
  'state.index.browse': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/state/{type}/{pattern}/{version}/{state}/index/{index}/browse`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      type: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      state: { type: 'path' },
      index: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
      take: { type: 'query' },
      open: { type: 'query' },
      direction: { type: 'query' },
      bookmark: { type: 'query' },
      filter: { type: 'query' },
    },
    openapi: {
      tags: ['state'],
      operationId: 'browseIndexRecords',
      description: 'Get state record',
    },
  },
  'state.index.browse.observe': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/state/{type}/{pattern}/{version}/{state}/index/{index}/browse/observe`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      type: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      state: { type: 'path' },
      index: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
      take: { type: 'query' },
      open: { type: 'query' },
      direction: { type: 'query' },
      bookmark: { type: 'query' },
      filter: { type: 'query' },
    },
    openapi: {
      tags: ['state'],
      description: 'Get state record',
      operationId: 'observeIndexRecords',
    },
  },
  'state.browse.observe': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/state/{type}/{pattern}/{version}/{state}/browse/observe`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      type: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      state: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
      take: { type: 'query' },
      open: { type: 'query' },
      direction: { type: 'query' },
      bookmark: { type: 'query' },
    },
    openapi: {
      tags: ['state'],
      description: 'Get state record',
      operationId: 'observeRecords',
    },
  },
  'state.get': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/state/{type}/{pattern}/{version}/{state}`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      type: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      state: { type: 'path' },
      id: { type: 'query' },
      token: { type: 'header' },
      args: { type: 'header' },
    },
    openapi: {
      tags: ['state'],
      operationId: 'getStateRecord',
      description: 'Get state record',
    },
  },
  'state.get.observe': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/state/{type}/{pattern}/{version}/{state}/observe`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      type: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      state: { type: 'path' },
      id: { type: 'query' },
      token: { type: 'header' },
      args: { type: 'header' },
    },
    openapi: {
      tags: ['state'],
      operationId: 'observeStateRecord',
      description: 'Observe state record',
    },
  },
  'state.iterate': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/state/{type}/{pattern}/{version}/{state}/iterate`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      type: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      state: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
      take: { type: 'query' },
      open: { type: 'query' },
      direction: { type: 'query' },
      bookmark: { type: 'query' },
    },
    openapi: {
      tags: ['state'],
      description: 'Iterate state record',
      operationId: 'iterateRecords',
    },
  },
  'state.index.iterate': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/state/{type}/{pattern}/{version}/{state}/index/{index}/iterate`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      type: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      state: { type: 'path' },
      index: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
      take: { type: 'query' },
      open: { type: 'query' },
      filter: { type: 'query' },
      direction: { type: 'query' },
      bookmark: { type: 'query' },
    },
    openapi: {
      tags: ['state'],
      operationId: 'iterateIndexRecords',
      description: 'Iterate state record',
    },
  },
  'process.result': {
    method: 'GET',
    path: `/api/{tenant}/{realm}/process/stream/{pattern}/{version}/result/{id}`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      id: { type: 'path' },
      token: { type: 'header' },
      args: { type: 'header' },
    },
    openapi: {
      tags: ['process'],
      operationId: 'getProcessResult',
      description: 'Get result of an event of a stream',
    },
  },
  'process.await': {
    method: 'POST',
    path: `/api/{tenant}/{realm}/process/aggregation/{pattern}/{version}/await`,
    mapping: {
      tenant: { type: 'path' },
      realm: { type: 'path' },
      pattern: { type: 'path' },
      version: { type: 'path' },
      eventId: { type: 'query' },
      token: { type: 'header' },
      args: { type: 'header' },
    },
    openapi: {
      tags: ['process'],
      operationId: 'awaitProcess',
      description: 'Await the processing until specified date',
    },
  },
});
